import type { RateBaseCriteria } from "../../../hooks/react-query/backOffice/pricingAndRating/useRateBaseCriteria";

/** Matches Figma reference: 1 group, 2 sequences (Client / State / CA, OR, WA) */
const standardCriteria: RateBaseCriteria = {
  groups: [
    {
      sequences: [
        {
          startTableName: "Client",
          endTableName: "Client",
          startFieldName: "State",
          endFieldName: "State",
          startLookup: "Shipper",
          endLookup: "Consignee",
          startMin: "CA, OR, WA",
          startMax: "CA, OR, WA",
          endMin: "CA, OR, WA",
          endMax: "CA, OR, WA",
          startLogic: "Like",
          endLogic: "Like",
          logicValue: "Not Like",
          reverseRange: true,
        },
        {
          startTableName: "Client",
          endTableName: "Client",
          startFieldName: "State",
          endFieldName: "State",
          startLookup: "Shipper",
          endLookup: "Consignee",
          startMin: "CA, OR, WA",
          startMax: "CA, OR, WA",
          endMin: "CA, OR, WA",
          endMax: "CA, OR, WA",
          startLogic: "Like",
          endLogic: "Like",
          logicValue: "Not Like",
          reverseRange: false,
        },
      ],
    },
  ],
  betweenGroupLogic: [],
};

/** Two groups with between-group logic */
const multiGroupCriteria: RateBaseCriteria = {
  groups: [
    {
      sequences: [
        {
          startTableName: "Client",
          endTableName: "Client",
          startFieldName: "State",
          endFieldName: "State",
          startLookup: "Shipper",
          endLookup: "Consignee",
          startMin: "CA, OR, WA",
          startMax: "CA, OR, WA",
          endMin: "CA, OR, WA",
          endMax: "CA, OR, WA",
          startLogic: "Like",
          endLogic: "Like",
          logicValue: "Not Like",
          reverseRange: true,
        },
        {
          startTableName: "Client",
          endTableName: "Client",
          startFieldName: "State",
          endFieldName: "State",
          startLookup: "Shipper",
          endLookup: "Consignee",
          startMin: "CA, OR, WA",
          startMax: "CA, OR, WA",
          endMin: "CA, OR, WA",
          endMax: "CA, OR, WA",
          startLogic: "Like",
          endLogic: "Like",
          logicValue: "Not Like",
          reverseRange: false,
        },
      ],
    },
    {
      sequences: [
        {
          startTableName: "Zone",
          endTableName: "Zone",
          startFieldName: "ZoneCode",
          endFieldName: "ZoneCode",
          startLookup: "Origin",
          endLookup: "Destination",
          startMin: "1",
          startMax: "50",
          endMin: "1",
          endMax: "50",
          startLogic: "Between",
          endLogic: "Between",
          logicValue: "AND",
          reverseRange: false,
        },
      ],
    },
  ],
  betweenGroupLogic: ["AND"],
};

/** Mileage-specific criteria */
const mileageCriteria: RateBaseCriteria = {
  groups: [
    {
      sequences: [
        {
          startTableName: "Geography",
          endTableName: "Geography",
          startFieldName: "State",
          endFieldName: "State",
          startLookup: "Origin",
          endLookup: "Destination",
          startMin: "TX",
          startMax: "TX",
          endMin: "CA",
          endMax: "CA",
          startLogic: "Equals",
          endLogic: "Equals",
          logicValue: "OR",
          reverseRange: false,
        },
        {
          startTableName: "Mileage",
          endTableName: "Mileage",
          startFieldName: "Miles",
          endFieldName: "Miles",
          startLookup: "Actual",
          endLookup: "Actual",
          startMin: "0",
          startMax: "500",
          endMin: "501",
          endMax: "10000",
          startLogic: "Between",
          endLogic: "Between",
          logicValue: "AND",
          reverseRange: false,
        },
      ],
    },
  ],
  betweenGroupLogic: [],
};

const rateBaseCriteriaMocks: Record<number, RateBaseCriteria> = {
  // Unit Rates / Per Weight Count IDs (no detail page yet, included for completeness)
  3: standardCriteria,
  6: standardCriteria,
  12: standardCriteria,
  15: standardCriteria,
  20: standardCriteria,
  22: standardCriteria,
  28: standardCriteria,
  29: standardCriteria,

  // Rateware XL Rate Base IDs
  1: standardCriteria,
  11: multiGroupCriteria,
  18: standardCriteria,
  25: multiGroupCriteria,
  32: standardCriteria,

  // Commodity Rate Base IDs
  2: standardCriteria,
  7: standardCriteria,
  8: multiGroupCriteria,
  16: standardCriteria,
  23: multiGroupCriteria,
  30: standardCriteria,

  // Truckload Rate Base IDs
  5: standardCriteria,
  10: multiGroupCriteria,
  17: standardCriteria,
  24: multiGroupCriteria,
  31: standardCriteria,

  // Flat Rate Base IDs
  9: standardCriteria,
  14: multiGroupCriteria,
  21: standardCriteria,
  27: multiGroupCriteria,

  // Rate Per Handling Unit IDs
  34: standardCriteria,
  35: multiGroupCriteria,

  // Mileage Rate Base IDs
  4: standardCriteria,
  13: mileageCriteria,
  19: standardCriteria,
  26: multiGroupCriteria,
  33: mileageCriteria,
};

export function getRateBaseCriteriaMock(
  id: number
): RateBaseCriteria | undefined {
  return rateBaseCriteriaMocks[id];
}
